import type { InterviewOutcomes, ReasonCount } from '../../services/usageApi';
import { StatTile } from './StatTile';
import { RankedBars } from './RankedBars';
import { SERIES_ERROR, SERIES_PRIMARY, formatCompact, formatHours } from './chartTokens';

type Changes = NonNullable<InterviewOutcomes['changes']>;

/** `accepted_other_offer` → `Accepted other offer`. Codes are for machines; this table is read. */
export function humaniseCode(code: string): string {
  if (code === 'not_given') return 'No reason given';
  const words = code.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const asBars = (rows: ReasonCount[]) =>
  rows.map((r) => ({ label: humaniseCode(r.code), value: r.count }));

function ReasonPair({
  title,
  intro,
  reasons,
  color,
  emptyMessage,
}: {
  title: string;
  intro: string;
  reasons: { candidate: ReasonCount[]; finn: ReasonCount[] };
  color: string;
  emptyMessage: string;
}) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
      <p className="mt-0.5 text-xs text-zinc-500">{intro}</p>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium text-zinc-600">The candidate</p>
          <div className="mt-2">
            <RankedBars
              items={asBars(reasons.candidate)}
              color={color}
              emptyMessage={emptyMessage}
            />
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-zinc-600">FINN (Log page or a request card)</p>
          <div className="mt-2">
            <RankedBars items={asBars(reasons.finn)} color={color} emptyMessage={emptyMessage} />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Who cancelled or moved the interview, and how (Sofia, Sep 11 2026: "how often interviews are
 * rescheduled and cancelled" — the previous panel had the totals but pooled candidate and FINN
 * changes under one heading that said "candidates", and staff reason codes such as
 * `interviewer_unavailable` were listed as the candidate's reasons).
 *
 * Built from booking RECORDS: `cancelSource` / `rescheduleSource` are stamped by the route that made
 * the change, so a FINN-side count here is a change a recruiter really made, not an event that may
 * have been lost. No `cancelledBy` travels — the proxy aggregates and the section shows counts.
 */
export function OutcomeChangesSection({ changes }: { changes: Changes }) {
  const cancelledTotal = changes.cancelled.candidate + changes.cancelled.finn;
  const movedTotal = changes.rescheduled.candidate + changes.rescheduled.finn;
  const share = (part: number, total: number) =>
    total === 0 ? '—' : `${Math.round((part / total) * 100)}%`;
  const distribution = changes.rescheduleCountDistribution;
  const distributionMax = Math.max(1, ...distribution.map((d) => d.count));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Cancelled by the candidate"
          value={formatCompact(changes.cancelled.candidate)}
          hint={`${share(changes.cancelled.candidate, cancelledTotal)} of cancellations`}
        />
        <StatTile
          label="Cancelled by FINN"
          value={formatCompact(changes.cancelled.finn)}
          hint={`${share(changes.cancelled.finn, cancelledTotal)} of cancellations`}
        />
        <StatTile
          label="Moved by the candidate"
          value={formatCompact(changes.rescheduled.candidate)}
          hint={`${share(changes.rescheduled.candidate, movedTotal)} of moved interviews`}
        />
        <StatTile
          label="Moved by FINN"
          value={formatCompact(changes.rescheduled.finn)}
          hint={`${share(changes.rescheduled.finn, movedTotal)} of moved interviews`}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Notice before a cancellation"
          value={formatHours(changes.cancelNoticeMedianHours)}
          hint={
            changes.cancelNoticeSampleSize === 0
              ? 'No dated cancellations yet'
              : `Median · ${formatCompact(changes.cancelNoticeSampleSize)} measured`
          }
        />
        <StatTile
          label="Cancelled inside 24 h"
          value={formatCompact(changes.cancelledWithin24h)}
          hint="Less than a day before the interview"
        />
        <StatTile
          label="Not written to Lever"
          value={formatCompact(changes.leverSyncFailed)}
          hint={
            changes.leverSyncFailed > 0
              ? 'Shown as ⚠ Not in Lever on the Log'
              : 'Every change reached Lever'
          }
        />
      </div>

      {/* Sorted by how many times, ascending: the long tail is the finding. */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-zinc-900">
          How often a moved interview was moved
        </h3>
        <p className="mt-0.5 text-xs text-zinc-500">
          Interviews moved once are normal; the same interview moved three or four times is a
          candidate we are close to losing.
        </p>
        <div className="mt-3 space-y-2" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {distribution.length === 0 && (
            <p className="text-sm text-zinc-500">Nothing was rescheduled.</p>
          )}
          {distribution.map((d) => (
            <div key={d.times} className="flex items-center gap-3 text-sm">
              <span className="w-24 shrink-0 text-zinc-700">
                {d.times >= 4 ? '4+ times' : `${d.times}×`}
              </span>
              <div className="h-3 flex-1 rounded bg-zinc-100">
                <div
                  className="h-3 rounded"
                  style={{
                    width: `${(d.count / distributionMax) * 100}%`,
                    background: SERIES_PRIMARY,
                  }}
                />
              </div>
              <span className="w-10 text-right text-zinc-900">{d.count}</span>
            </div>
          ))}
        </div>
      </section>

      <ReasonPair
        title="Why interviews were cancelled"
        intro="Two vocabularies: the candidate picks from their manage page, FINN from the Log page or a request card. “Accepted another offer” is a finding about hiring, “role closed” about planning."
        reasons={changes.cancelReasons}
        color={SERIES_ERROR}
        emptyMessage="No cancellations in this range."
      />
      <ReasonPair
        title="Why interviews were moved"
        intro="Reschedule reasons, most common first, by who moved it."
        reasons={changes.rescheduleReasons}
        color={SERIES_PRIMARY}
        emptyMessage="Nothing was rescheduled."
      />
    </div>
  );
}
